import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { FormattedNumber, useIntl } from 'react-intl'

import {
  formatAddressShort,
  getDimensionPalletSpaces,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  activeSellerSubscriptionServiceQuery,
  getSellerCollectsConfig,
  type LeanGroup,
  type OrchestratedCommonListingDetails,
  type OrchestratedListingDetails,
  type SellerCollectsConfig,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import type { GroupDimensions } from '@b-stock/listing-api-client'
import {
  GroupDtoTypeEnum,
  StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum as ShippingArrangedBy,
} from '@b-stock/listing-api-client'
import type { SoldAs } from '@b-stock/order-api-client'

import { getFeeModel } from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import {
  getListingSaleKindForBuyer,
  type SaleKind,
} from '@/buyer-portal/helpers/sellerCollectsSaleKind'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { FeatureFlags } from '@/helpers/featureFlags'

import { calculateShipmentDetails } from './utils'

export type DimensionColumnVisibility = {
  palletSpaces: boolean
  weight: boolean
}

export type ShipmentDimensionRow = {
  dim: GroupDimensions
  groupType: GroupDtoTypeEnum
  key: string
  palletSpaces: number | null | undefined
}

/**
 * Shared derivation for the shipment-details presentations: the legacy V1
 * `Shipment` section and the redesigned V2 `ShipmentDetailsCard`. Both forks
 * render their own markup but compute their values here so the two stay in
 * sync.
 */
export const useShipmentDetails = (
  details: OrchestratedListingDetails,
  groups: LeanGroup[]
) =>
  useMemo(() => {
    const {
      numberOfUnits,
      showPalletSpaces,
      totalPalletSpaces,
      totalWeightInGrams,
    } = calculateShipmentDetails(groups)

    const firstGroup = groups.length ? groups[0] : null
    const allowedShipmentTypes = details.shipping?.allowedShipmentTypes ?? []
    const isFloorLoaded = firstGroup?.type === GroupDtoTypeEnum.FloorLoaded

    const dimensionRows: ShipmentDimensionRow[] = groups.flatMap((group) =>
      (group.dimensions ?? []).map((dim, index) => ({
        dim,
        groupType: group.type,
        key: `${group._id}-${index}`,
        palletSpaces: getDimensionPalletSpaces(group.type, dim),
      }))
    )

    return {
      allowedShipmentTypes,
      columnVisibility: {
        palletSpaces: showPalletSpaces,
        weight: totalWeightInGrams > 0,
      } satisfies DimensionColumnVisibility,
      dimensionRows,
      firstGroup,
      groups,
      isFloorLoaded,
      isHazardous: details.shipping?.hazmat?.isHazardous,
      note: details.shipping?.note,
      numberOfUnits,
      // A PARCEL dimension's count is boxes, so only an all-pallet shipment
      // can call its unit count pallets.
      isAllPallets:
        groups.length > 0 &&
        groups.every((group) => group.type === GroupDtoTypeEnum.Pallet),
      // The unit count is meaningful for non-floor-loaded shipments, or any
      // time there is more than one unit.
      showNumberOfUnits:
        (!!numberOfUnits &&
          firstGroup?.type !== GroupDtoTypeEnum.FloorLoaded) ||
        numberOfUnits > 1,
      showOnlyMessage: allowedShipmentTypes.length === 1,
      showPalletSpaces,
      totalPalletSpaces,
      totalWeightInGrams,
      transportMode: details.shipping?.transportMode,
      truckCount: details.shipping?.truckCount,
    }
  }, [details, groups])

const scNs = 'Listing.shipping.sellerCollects'

type SellerCollectsShippingCopy = {
  /** Sentence describing who moves the goods and when it is arranged. */
  descriptionId: string
  /** Label for the rate row, replacing "Estimated shipping". */
  rateLabelId: string
  /** Shipment-grid rows, per the design's freight/payment split. */
  arrangedById: string
  collectedById: string
  /**
   * Set only where the design splits the description in two: a bold line
   * naming the pickup place, then the coordination sentence under it. The
   * combined `descriptionId` stays for the surfaces that show one line.
   */
  pickupLineId?: string
  coordinationId?: string
} & (
  | { showQuotedAmount: true; rateValueId?: never }
  /** Copy shown in place of an amount B-Stock does not have. */
  | { showQuotedAmount: false; rateValueId: string }
)

const BUYER_ARRANGED = {
  descriptionId: `${scNs}.buyer.description`,
  pickupLineId: `${scNs}.buyer.pickupFrom`,
  // The same sentence the no-origin variant leads with, which is exactly the
  // half of `description` that follows the pickup place.
  coordinationId: `${scNs}.buyer.descriptionNoOrigin`,
  rateLabelId: `${scNs}.buyer.rateLabel`,
  rateValueId: `${scNs}.buyer.rateValue`,
  showQuotedAmount: false,
  arrangedById: `${scNs}.buyer.arrangedBy`,
  collectedById: `${scNs}.buyer.collectedBy`,
} satisfies SellerCollectsShippingCopy

// Buyer-arranged copy is the only copy that names the pickup location.
const BUYER_ARRANGED_NO_ORIGIN = {
  ...BUYER_ARRANGED,
  descriptionId: `${scNs}.buyer.descriptionNoOrigin`,
  pickupLineId: undefined,
  coordinationId: undefined,
} satisfies SellerCollectsShippingCopy

const SELLER_DELIVERS = {
  descriptionId: `${scNs}.sellerDelivers.description`,
  rateLabelId: `${scNs}.sellerDelivers.rateLabel`,
  rateValueId: `${scNs}.sellerDelivers.rateValue`,
  showQuotedAmount: false,
  arrangedById: `${scNs}.sellerDelivers.arrangedBy`,
  collectedById: `${scNs}.sellerDelivers.collectedBy`,
} satisfies SellerCollectsShippingCopy

// The only mode with a B-Stock rate — but the seller still bills it with the
// goods, so the amount is shown with that stated rather than left to imply
// B-Stock collects it.
const BOOKED_THROUGH_BSTOCK = {
  descriptionId: `${scNs}.bookedThroughBstock.description`,
  rateLabelId: `${scNs}.bookedThroughBstock.rateLabel`,
  showQuotedAmount: true,
  arrangedById: `${scNs}.bookedThroughBstock.arrangedBy`,
  collectedById: `${scNs}.bookedThroughBstock.collectedBy`,
} satisfies SellerCollectsShippingCopy

// A real state, not an error: a storefront with no block, or one carrying a
// mode this build predates, still needs shipping copy. Naming the seller as
// the contact is honest where inventing terms would not be.
const UNCONFIGURED = {
  descriptionId: `${scNs}.unconfigured.description`,
  rateLabelId: `${scNs}.unconfigured.rateLabel`,
  rateValueId: `${scNs}.unconfigured.rateValue`,
  showQuotedAmount: false,
  arrangedById: `${scNs}.unconfigured.arrangedBy`,
  collectedById: `${scNs}.unconfigured.collectedBy`,
} satisfies SellerCollectsShippingCopy

/**
 * The descriptions that promise something "after you win", and what they read
 * on a listing bought outright.
 *
 * Only these two modes make the buyer wait for an outcome; `SELLER_DELIVERS`
 * and `BOOKED_THROUGH_BSTOCK` state who moves the goods and never mention one,
 * so they have a single string either way.
 *
 * Written out rather than derived from the id so both halves stay greppable
 * and the orphan-string scan can see them.
 *
 * Keyed by `descriptionId` but read for `coordinationId` too, which works
 * only because every `coordinationId` reuses a description id. A split line
 * given an id of its own needs an entry here, or a bought listing silently
 * keeps the "after you win" wording.
 */
const PURCHASE_DESCRIPTION_IDS: Record<string, string> = {
  [`${scNs}.buyer.description`]: `${scNs}.purchase.buyer.description`,
  [`${scNs}.buyer.descriptionNoOrigin`]: `${scNs}.purchase.buyer.descriptionNoOrigin`,
  [`${scNs}.unconfigured.description`]: `${scNs}.purchase.unconfigured.description`,
}

/** Same copy, in the tense of this sale. */
const inSaleKind = (
  copy: SellerCollectsShippingCopy,
  saleKind: SaleKind
): SellerCollectsShippingCopy => {
  if (saleKind !== 'purchase') return copy

  const purchaseId = PURCHASE_DESCRIPTION_IDS[copy.descriptionId]
  // The split lines carry the tense too, or the card would promise a win on a
  // listing the buyer has already bought.
  const purchaseCoordinationId = copy.coordinationId
    ? PURCHASE_DESCRIPTION_IDS[copy.coordinationId]
    : undefined

  if (!purchaseId && !purchaseCoordinationId) return copy

  return {
    ...copy,
    ...(purchaseId ? { descriptionId: purchaseId } : {}),
    ...(purchaseCoordinationId
      ? { coordinationId: purchaseCoordinationId }
      : {}),
  }
}

const shippingCopyFor = (
  config: SellerCollectsConfig | undefined,
  hasOrigin: boolean,
  saleKind: SaleKind = 'bid'
): SellerCollectsShippingCopy => {
  switch (config?.shippingArrangedBy) {
    case ShippingArrangedBy.Buyer:
      return inSaleKind(
        hasOrigin ? BUYER_ARRANGED : BUYER_ARRANGED_NO_ORIGIN,
        saleKind
      )
    case ShippingArrangedBy.SellerDelivers:
      return SELLER_DELIVERS
    case ShippingArrangedBy.BookedThroughBstock:
      return BOOKED_THROUGH_BSTOCK
    default:
      return inSaleKind(UNCONFIGURED, saleKind)
  }
}

type SellerCollectsShippingDetails = SellerCollectsShippingCopy & {
  /** False for ordinary listings and whenever the flag is off. */
  isSellerCollects: boolean
  /** Already falls back to generic wording; safe to interpolate directly. */
  sellerName: string
  /** Undefined when the lot has no usable address. */
  origin?: string
  /** True only once the seller's plan is confirmed to charge a buyer's premium. */
  hasFee: boolean
}

/**
 * Who arranges, quotes and bills freight on a Seller Collects listing.
 *
 * Three of the four Seller Collects storefronts are buyer-arranged or
 * seller-delivered, so B-Stock has no rate to show for them — hence the PRD's
 * "why is shipping Unavailable for 3 of 4 sellers". The fix is to say who owns
 * freight rather than render an empty value.
 *
 * Shared by the shipping card and both shipment grids so they cannot state
 * different terms for the same lot. Returns a presentation for every listing;
 * callers gate on `isSellerCollects`.
 */
export const useSellerCollectsShipping = (
  // The common base, not the auction/offering union: this reads only `lot`,
  // `seller`, `sellerCollects` and the storefront, so the offer confirmation
  // modal can use it too.
  // Undefined where the caller's listing is itself optional; the result then
  // describes no listing rather than forcing a conditional hook on them.
  listing: OrchestratedCommonListingDetails | undefined,
  // The viewer's own order for this listing, when they have one. Takes
  // priority over the live window below — see getListingSaleKindForBuyer.
  order?: { listing?: { soldAs?: SoldAs | null } | null } | null
): SellerCollectsShippingDetails => {
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  const { formatMessage } = useIntl()
  // Resolved outside the memo because it is time-dependent: a Buy Now window
  // closing while the page is open would otherwise leave this card on the
  // purchase wording while the banner and the tag, which re-read it every
  // render, switch to the bid wording.
  const saleKind = getListingSaleKindForBuyer(listing, order)
  const isSellerCollects =
    sellerCollectsEnabled && listing?.sellerCollects === true
  const isLoggedIn = useIsLoggedIn()
  // A signed-out visitor cannot resolve the seller's plan, same as the
  // banner — the query is skipped and the fee suffix stays off rather than
  // guessing. Keyed on the same accountId the banner uses, so react-query
  // dedupes the two calls on the same listing page.
  const {
    data: subscription,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
  } = activeSellerSubscriptionServiceQuery.useQuery(listing?.accountId ?? '', {
    enabled: isSellerCollects && isLoggedIn,
  })
  const hasFee =
    getFeeModel({
      isLoggedIn,
      isLoading: isSubscriptionLoading,
      isError: isSubscriptionError,
      buyersPremium: subscription?.buyersPremium,
    }) === 'feeBearing'

  return useMemo(() => {
    if (!listing) {
      return {
        ...shippingCopyFor(undefined, false),
        isSellerCollects: false,
        sellerName: formatMessage({
          id: 'Listing.shipping.sellerCollects.theSeller',
        }),
        hasFee: false,
      }
    }

    // Non-nullable on the type, but can format to an empty string when the
    // lot has no usable city/state.
    const origin = formatAddressShort(listing.lot.location, true) || undefined

    return {
      ...shippingCopyFor(
        getSellerCollectsConfig(listing),
        origin !== undefined,
        saleKind
      ),
      isSellerCollects,
      sellerName:
        listing.seller.storefront?.name ||
        listing.seller.account.displayName ||
        formatMessage({ id: 'Listing.shipping.sellerCollects.theSeller' }),
      origin,
      hasFee,
    }
  }, [listing, saleKind, isSellerCollects, hasFee, formatMessage])
}

const formatStrong = (chunks: ReactNode) => <strong>{chunks}</strong>

// WARNING: Duplication not actually eliminated yet - see also `GroupDimensionRow` in fe-core
export const renderDimensionsContent = (
  groupType: GroupDtoTypeEnum,
  dim: GroupDimensions
) => (
  <FormattedMessage
    id={`Auction.shipment.details.GroupType.${groupType}`}
    values={{
      dimensions:
        dim.depth?.value || dim.width?.value || dim.height?.value ? (
          <FormattedMessage
            id="Auction.shipment.details.dimensionsValue"
            values={{
              b: formatStrong,
              height: dim.height?.value ?? 0,
              heightUnit: dim.height?.units.toLowerCase() ?? '',
              length: dim.depth?.value ?? 0,
              lengthUnit: dim.depth?.units.toLowerCase() ?? '',
              width: dim.width?.value ?? 0,
              widthUnit: dim.width?.units.toLowerCase() ?? '',
            }}
          />
        ) : (
          // Special string used for matching in the localization to omit the " at {dimensions}" text
          'NONE'
        ),
      unitCount: dim.count || 0,
    }}
  />
)

export const renderWeightContent = (dim: GroupDimensions) =>
  dim.weight ? (
    <>
      <FormattedNumber value={dim.weight.value} />{' '}
      <FormattedMessage
        id={`Auction.shipment.details.weight.${dim.weight.units}`}
        values={{ weight: dim.weight.value }}
      />
    </>
  ) : (
    '-'
  )

export const renderPalletSpacesContent = (
  palletSpaces: number | null | undefined
) => (palletSpaces ? <FormattedNumber value={palletSpaces} /> : '-')
